/**
 * Query-key factories (WEBD-17).
 *
 * Every React Query key in the app comes from one of these factories — the
 * `queryKey: [` literal is lint-banned everywhere else. Conventions:
 *
 * - Each domain's `all` is the prefix every key in that domain starts with,
 *   so `invalidate(qc, domain)` (shared/api/invalidation.ts) can target the
 *   whole domain with one prefix match.
 * - Factories with more than one input take an input object, and every
 *   input that changes the response is in the key.
 * - Keys are deterministic: arrays are sorted inside the factory, on a copy
 *   (the caller's array is never mutated).
 */

import type { TaxLossHarvestPlanRequest } from '@luminaria/shared';
import type { FullTaxCalculationInput } from '../hooks/useTaxCalculation';
import type { ExternalIncomeEstimateRequest } from './externalIncomeApi';
import { normalizeHarvestPlanRequest, normalizeTaxCalculationInput } from './taxApi';
import type { TransactionListParams } from './transactionsApi';

/** Sorted copy of an id/symbol list; `null` for "no filter" so the key is stable. */
function sortedList(values: readonly string[] | undefined): string[] | null {
  if (!values || values.length === 0) return null;
  return [...values].sort();
}

// ---------------------------------------------------------------------------
// User / auth
// ---------------------------------------------------------------------------

export const userKeys = {
  all: ['user'] as const,
  profile: () => ['user', 'profile'] as const,
};

// ---------------------------------------------------------------------------
// Portfolios, accounts, holdings, transactions
// ---------------------------------------------------------------------------

export const portfolioKeys = {
  all: ['portfolios'] as const,
  list: () => ['portfolios', 'list'] as const,
  holdingsSummary: (input: { portfolioId: string; accountIds?: readonly string[] }) =>
    ['portfolios', 'holdings-summary', input.portfolioId, { accountIds: sortedList(input.accountIds) }] as const,
  accountsView: (portfolioId: string) => ['portfolios', 'accounts-view', portfolioId] as const,
};

export const settlementFundKeys = {
  all: ['settlementFund'] as const,
  detect: (accountId: string) => ['settlementFund', 'detect', accountId] as const,
  suggestions: (portfolioId: string) => ['settlementFund', 'suggestions', portfolioId] as const,
};

export const holdingKeys = {
  all: ['holdings'] as const,
  byAccount: (accountId: string) => ['holdings', 'by-account', accountId] as const,
  byPortfolio: (portfolioId: string) => ['holdings', 'by-portfolio', portfolioId] as const,
};

export const transactionKeys = {
  all: ['transactions'] as const,
  byAccount: (accountId: string, params?: TransactionListParams) =>
    ['transactions', { accountId }, params ?? {}] as const,
  byPortfolio: (portfolioId: string, params?: TransactionListParams) =>
    ['transactions', { portfolioId }, params ?? {}] as const,
  detail: (transactionId: string) => ['transactions', 'detail', transactionId] as const,
  corporateActionsNeedingReview: () => ['transactions', 'corporate-actions', 'needs-review'] as const,
};

// ---------------------------------------------------------------------------
// Income, external income, tax
// ---------------------------------------------------------------------------

export const incomeKeys = {
  all: ['income'] as const,
  byPortfolio: (portfolioId: string) => ['income', 'by-portfolio', portfolioId] as const,
  breakdowns: (input: { portfolioId: string; period: number | 'all'; excludeNonTaxable: boolean }) =>
    ['income', 'breakdowns', { ...input }] as const,
};

export const incomeProjectionKeys = {
  all: ['incomeProjections'] as const,
  /**
   * The server-derived projection for a portfolio's selected accounts
   * (WEBD-23). Quantities and dividend data are read server-side, so the
   * selection is the whole input; holdings/transaction writes reach it
   * through `incomeProjectionKeys.all` in the invalidation map.
   */
  portfolio: (input: { portfolioId: string; accountIds: readonly string[] }) =>
    ['incomeProjections', 'portfolio', input.portfolioId, { accountIds: sortedList(input.accountIds) }] as const,
};

export const externalIncomeKeys = {
  all: ['external-income'] as const,
  list: () => ['external-income', 'list'] as const,
  byType: (year: number | undefined) => ['external-income', 'by-type', year ?? null] as const,
  /** The form's live preview: the whole (normalized) request is the key (WEBD-06). */
  estimate: (request: ExternalIncomeEstimateRequest) => ['external-income', 'estimate', request] as const,
};

export const taxPaymentKeys = {
  all: ['tax-payments'] as const,
  list: () => ['tax-payments', 'list'] as const,
  byYear: (taxYear: number) => ['tax-payments', 'by-year', taxYear] as const,
};

export const taxKeys = {
  all: ['taxCalculation'] as const,
  /**
   * The whole normalized request body is the key (every input changes the
   * result), plus the saved settings the server reads alongside it (WEBD-01).
   */
  full: (input: FullTaxCalculationInput, savedSettings?: unknown) =>
    ['taxCalculation', 'full', normalizeTaxCalculationInput(input), { savedSettings: savedSettings ?? null }] as const,
  /**
   * The harvest plan reads the same saved settings as the full estimate, and
   * sits under `taxKeys.all` so whatever invalidates the estimate (a
   * transaction edit, a sync) invalidates the plan with it.
   */
  harvestPlan: (input: TaxLossHarvestPlanRequest, savedSettings?: unknown) =>
    ['taxCalculation', 'harvestPlan', normalizeHarvestPlanRequest(input), { savedSettings: savedSettings ?? null }] as const,
};

// ---------------------------------------------------------------------------
// Calculations: realized gains, lots, cost basis
// ---------------------------------------------------------------------------

export const calculationKeys = {
  all: ['calculations'] as const,
  realizedGains: (input: { accountId: string | undefined; taxYear: number }) =>
    ['calculations', 'realized-gains', input.accountId ?? 'all', input.taxYear] as const,
  realizedGainsYears: (accountId: string) => ['calculations', 'realized-gains-years', accountId] as const,
  /** Portfolio-wide realized gains (all years) behind the Gains page. */
  portfolioRealizedGains: (input: { portfolioId: string; accountIds?: readonly string[] }) =>
    ['calculations', 'portfolio-realized-gains', input.portfolioId, { accountIds: sortedList(input.accountIds) }] as const,
};

export const lotKeys = {
  all: ['lot-assignments'] as const,
  /** `{ result, accountName }` per account — the stock-chart lot markers (useSymbolLots). */
  bySymbol: (input: { accountId: string; symbol: string }) =>
    ['lot-assignments', 'symbol', input.accountId, input.symbol] as const,
};

export const costBasisKeys = {
  all: ['costBasis'] as const,
  /** The Cost Basis page's portfolio reconciliation (GET /portfolios/:id/cost-basis). */
  snapshot: (portfolioId: string) => ['costBasis', 'snapshot', portfolioId] as const,
};

// ---------------------------------------------------------------------------
// Performance, daily change, allocation, rebalancer
// ---------------------------------------------------------------------------

export const performanceKeys = {
  all: ['performance'] as const,
  data: (input: { portfolioId: string; year: number | undefined; accountIds?: readonly string[] }) =>
    ['performance', 'data', input.portfolioId, input.year ?? 'all', { accountIds: sortedList(input.accountIds) }] as const,
  benchmark: (input: { portfolioId: string; window: string; symbols: readonly string[] }) =>
    ['performance', 'benchmark', input.portfolioId, input.window, { symbols: sortedList(input.symbols) }] as const,
};

export const dailyChangeKeys = {
  all: ['dailyChange'] as const,
  byPortfolio: (portfolioId: string) => ['dailyChange', portfolioId] as const,
};

export const allocationKeys = {
  all: ['portfolioAllocation'] as const,
  byPortfolio: (input: { portfolioId: string; accountIds?: readonly string[] }) =>
    ['portfolioAllocation', input.portfolioId, { accountIds: sortedList(input.accountIds) }] as const,
};

export const rebalancerKeys = {
  all: ['rebalancer'] as const,
  holdings: (input: { portfolioId: string; accountIds?: readonly string[] }) =>
    ['rebalancer', 'holdings', input.portfolioId, { accountIds: sortedList(input.accountIds) }] as const,
};

export const targetProfileKeys = {
  all: ['target-allocation-profiles'] as const,
  list: () => ['target-allocation-profiles', 'list'] as const,
};

// ---------------------------------------------------------------------------
// Market data
// ---------------------------------------------------------------------------

export const marketKeys = {
  all: ['market'] as const,
  quotesAll: ['market', 'quotes'] as const,
  quote: (symbol: string) => ['market', 'quotes', 'single', symbol] as const,
  quotes: (symbols: readonly string[]) => ['market', 'quotes', 'batch', (sortedList(symbols) ?? []).join(',')] as const,
  validateSymbol: (symbol: string) => ['market', 'validate-symbol', symbol] as const,
  optionsChainAll: ['market', 'optionsChain'] as const,
  /** One chain request: the symbol's full chain, or one expiration (unix seconds). */
  optionsChain: (input: { symbol: string; expiration?: number }) =>
    ['market', 'optionsChain', input.symbol.trim().toUpperCase(), input.expiration ?? 'all'] as const,
  /** One expiration's chain as the batch endpoint returns it (no expiration list). */
  optionsChainBatchEntry: (input: { symbol: string; expiration: number }) =>
    ['market', 'optionsChain', input.symbol.trim().toUpperCase(), 'batch', input.expiration] as const,
  movers: () => ['market', 'movers'] as const,
  news: (input: { limit: number; sources?: readonly string[] }) =>
    ['market', 'news', input.limit, { sources: sortedList(input.sources) }] as const,
  symbolNews: (input: { symbol: string; limit: number }) => ['market', 'symbol-news', input.symbol, input.limit] as const,
  chart: (input: { symbol: string; range: string }) => ['market', 'chart', input.symbol, input.range] as const,
  cacheConfig: () => ['market', 'cache-config'] as const,
  quoteCacheStats: () => ['market', 'quote-cache-stats'] as const,
  securityMetadata: (symbols: readonly string[]) => ['market', 'security-metadata', { symbols: sortedList(symbols) }] as const,
};

/** Holdings-derived dividend data (upcoming ex-dates, Income Estimator per-symbol dividend info). */
export const dividendKeys = {
  all: ['dividends'] as const,
  upcoming: (days: number) => ['dividends', 'upcoming', days] as const,
};

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

export const optionsKeys = {
  all: ['options'] as const,
  config: () => ['options', 'config'] as const,
  ccCoverageAll: ['options', 'cc-coverage'] as const,
  ccCoverage: (input: { portfolioId: string; accountId: string; symbol: string }) =>
    ['options', 'cc-coverage', input.portfolioId, input.accountId, input.symbol] as const,
};

export const strategyKeys = {
  all: ['strategies'] as const,
  list: () => ['strategies', 'list'] as const,
};

// ---------------------------------------------------------------------------
// Watchlists, messages, API keys
// ---------------------------------------------------------------------------

export const watchlistKeys = {
  all: ['watchlists'] as const,
  list: () => ['watchlists', 'list'] as const,
};

/** Message list filters. */
export interface MessageListFilters {
  status?: string;
  type?: string;
  limit?: number;
}

export const messageKeys = {
  all: ['messages'] as const,
  list: (filters?: MessageListFilters) => ['messages', 'list', filters ?? {}] as const,
  unreadCount: () => ['messages', 'unread-count'] as const,
};

export const apiKeyKeys = {
  all: ['api-keys'] as const,
  list: () => ['api-keys', 'list'] as const,
};

/** Passkeys live in Supabase Auth, not the Luminaria API. */
export const passkeyKeys = {
  all: ['passkeys'] as const,
  list: () => ['passkeys', 'list'] as const,
};

// ---------------------------------------------------------------------------
// SnapTrade
// ---------------------------------------------------------------------------

export const snaptradeKeys = {
  all: ['snaptrade'] as const,
  status: () => ['snaptrade', 'status'] as const,
  connections: () => ['snaptrade', 'connections'] as const,
  pendingAccounts: (authorizationId: string) => ['snaptrade', 'pending-accounts', authorizationId] as const,
  brokerages: () => ['snaptrade', 'brokerages'] as const,
  debug: () => ['snaptrade', 'debug'] as const,
  adminUsers: () => ['snaptrade', 'admin', 'users'] as const,
};

// ---------------------------------------------------------------------------
// Mutation keys (for useMutationState / isMutating filters)
// ---------------------------------------------------------------------------

export const mutationKeys = {
  syncAccount: ['snaptrade', 'sync'] as const,
  refreshConnection: ['snaptrade', 'refresh-connection'] as const,
  refreshTransactions: ['snaptrade', 'refresh-transactions'] as const,
  revokeBrokerage: ['snaptrade', 'revoke'] as const,
};
